import React, { useEffect, useRef, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import { resolveTabAccess } from '../auth/pageAccess';
import AdminNavTabs from '../components/AdminNavTabs';
// NOTE: These two modules are implemented by sibling tasks (the extraction
// client in task 6, the editable preview screen in task 8). This page is a
// client of both per their documented contracts; it never talks to the
// provider directly and never persists anything itself.
import { extractMenu } from '../services/menuImportClient';
import { extractMenuFromUrl } from '../services/menuImportClient';
import PreviewEditScreen from '../components/PreviewEditScreen';
import './AdminDashboard.css';

const PROJECT_NAME = process.env.REACT_APP_PROJECT_NAME || 'AppDemo';

// Upload constraints (Req 1.2-1.5). Kept in sync with the server-side limits in
// MenuExtractFunction; the backend re-validates as defense in depth.
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB (Req 1.4)
const MAX_UPLOAD_MB = 10;
const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const ACCEPTED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];
const SUPPORTED_TYPES_LABEL = 'JPG, PNG, or PDF';

// The `accept` attribute for the native file picker. Lists both MIME types and
// extensions so browsers that only match on one still filter correctly.
const FILE_ACCEPT_ATTR = 'image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf';

const fileExtension = (name) => {
  const dot = String(name || '').lastIndexOf('.');
  if (dot < 0) return '';
  return String(name).slice(dot + 1).toLowerCase();
};

/**
 * Client-side type + size validation for a single selected file (Req 1.2-1.5).
 * Returns a user-facing rejection message, or '' when the file is acceptable.
 * The message names the supported types (Req 1.3) or the max size (Req 1.5).
 */
export function validateUploadFile(file) {
  if (!file) return 'Please choose a file to upload.';

  const type = String(file.type || '').toLowerCase();
  const ext = fileExtension(file.name);
  const typeOk = ACCEPTED_MIME_TYPES.includes(type) || ACCEPTED_EXTENSIONS.includes(ext);
  if (!typeOk) {
    return `Unsupported file type. Supported types are ${SUPPORTED_TYPES_LABEL}.`;
  }

  if (Number(file.size) > MAX_UPLOAD_BYTES) {
    return `File is too large. The maximum allowed size is ${MAX_UPLOAD_MB} MB.`;
  }

  return '';
}

export default function MenuImportPage() {
  const navigate = useNavigate();
  const statusRef = useRef(null);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');

  // Tab: 'file' (existing upload) or 'url' (new URL import). Defaults to 'file'
  // so the existing flow is unchanged for existing users.
  const [importTab, setImportTab] = useState('file');

  // --- URL import state (new — does NOT touch any existing file state below) ---
  const [urlInput, setUrlInput] = useState('');
  const [urlError, setUrlError] = useState('');
  const [urlExtracting, setUrlExtracting] = useState(false);
  const [urlExtractError, setUrlExtractError] = useState(null);

  // Selected file + client-side validation feedback (Req 1.1-1.5).
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationError, setValidationError] = useState('');

  // Extraction lifecycle (Req 1.6, 5.1). `extracting` drives the progress
  // indicator; `extractError` holds a graceful failure with recovery options.
  const [extracting, setExtracting] = useState(false);
  // Per-page progress for multi-page PDFs (split + extracted one page at a time
  // to respect the free-tier rate limit). null for single-request extractions.
  const [extractProgress, setExtractProgress] = useState(null); // { current, total, waiting }
  const [extractedMenu, setExtractedMenu] = useState(null);
  // Phase 2 glue: the extraction result may carry embedded image URLs and a
  // Flat_Scan flag (backend fields added in task 13.1). We hold them alongside
  // the menu and forward them to the preview for per-item association (Req 10.2,
  // 10.4). Absent in Phase 1 → an empty list / false, so no photo UI is shown.
  const [extractedImages, setExtractedImages] = useState([]);
  const [extractedFlatScan, setExtractedFlatScan] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);
  const [extractError, setExtractError] = useState(null); // { message, canRetry, canEnterManually }

  // Auth + access guard, mirroring the other admin pages. Importing creates
  // products, so this page is gated on the Products tab.
  useEffect(() => {
    const init = async () => {
      try {
        const session = await Auth.currentSession();
        const access = await resolveTabAccess(session, 'Products');
        if (!access.allowed) {
          navigate(access.isPosAdmin ? '/admin/channels/pos/queue' : '/admin/login');
          return;
        }
        const user = await Auth.currentAuthenticatedUser();
        setUsername(user.username || user.attributes?.email || 'Admin');
      } catch (err) {
        navigate('/admin/login');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [navigate]);

  // Bring any status/error message into view when it changes.
  useEffect(() => {
    if ((validationError || extractError) && statusRef.current) {
      statusRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [validationError, extractError]);

  const resetSelection = () => {
    setSelectedFile(null);
    setValidationError('');
    setExtractError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setExtractError(null);
    if (!file) {
      setSelectedFile(null);
      setValidationError('');
      return;
    }
    const message = validateUploadFile(file);
    if (message) {
      // Reject the file and surface the supported-types / max-size message
      // (Req 1.3, 1.5). Clear the input so the same file can be re-picked.
      setSelectedFile(null);
      setValidationError(message);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setValidationError('');
    setSelectedFile(file);
  };

  const runExtraction = async () => {
    if (!selectedFile) return;
    setExtracting(true);
    setExtractProgress(null);
    setExtractError(null);
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();

      // The client resolves an ExtractResult for every non-abort outcome and
      // never throws on HTTP/quota/network errors (design Component 4). For a
      // multi-page PDF it reports per-page progress so the (necessarily slower)
      // rate-limited run doesn't look hung.
      const result = await extractMenu(selectedFile, token, {
        onProgress: (p) => setExtractProgress(p),
      });

      if (result && result.ok && result.menu) {
        // Success: hand the Extracted_Menu to the editable preview (Req 5.1),
        // along with any embedded images / Flat_Scan flag the backend returned
        // so the preview can offer per-item image association (Req 10.2, 10.4).
        setExtractedMenu(result.menu);
        setExtractedImages(Array.isArray(result.images) ? result.images : []);
        setExtractedFlatScan(result.flatScan === true);
        setManualEntry(false);
        return;
      }

      // Graceful failure: surface the message with the recovery options the
      // client reports (Req 9.1, 9.2). Default both recovery paths on when the
      // client omits them so the admin is never stuck.
      setExtractError({
        message: (result && result.error) || 'We could not extract the menu. Please try again or enter it manually.',
        canRetry: result ? result.canRetry !== false : true,
        canEnterManually: result ? result.canEnterManually !== false : true,
      });
    } catch (err) {
      // A superseded request may reject with AbortError; ignore it. Any other
      // unexpected throw still degrades to a graceful, recoverable error.
      if (err && err.name === 'AbortError') return;
      setExtractError({
        message: 'Something went wrong while extracting the menu. Please try again or enter it manually.',
        canRetry: true,
        canEnterManually: true,
      });
    } finally {
      setExtracting(false);
      setExtractProgress(null);
    }
  };

  const handleEnterManually = () => {
    // Skip extraction and open the preview with an empty menu so the admin can
    // build it by hand (Req 9.2 manual-entry fallback).
    setExtractError(null);
    setExtractedMenu({ categories: [] });
    // Manual entry starts from an empty menu with no images (Req 9.2).
    setExtractedImages([]);
    setExtractedFlatScan(false);
    setManualEntry(true);
  };

  const handleStartOver = () => {
    setExtractedMenu(null);
    setExtractedImages([]);
    setExtractedFlatScan(false);
    setManualEntry(false);
    resetSelection();
    // Also reset URL tab state so Start Over works from either tab
    setUrlInput('');
    setUrlError('');
    setUrlExtractError(null);
  };

  // --- URL import handler (new — completely separate from file extraction) ---
  const runUrlExtraction = async () => {
    const trimmed = urlInput.trim();
    if (!trimmed) {
      setUrlError('Please enter a URL.');
      return;
    }
    if (!/^https?:\/\/.+/i.test(trimmed)) {
      setUrlError('Please enter a valid http:// or https:// URL.');
      return;
    }
    setUrlError('');
    setUrlExtracting(true);
    setUrlExtractError(null);
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      const result = await extractMenuFromUrl(trimmed, token, {});
      if (result && result.ok && result.menu) {
        setExtractedMenu(result.menu);
        setExtractedImages(Array.isArray(result.images) ? result.images : []);
        setExtractedFlatScan(result.flatScan === true);
        setManualEntry(false);
        return;
      }
      setUrlExtractError({
        message: (result && result.error) || 'Could not extract the menu. Please try again or enter it manually.',
        canRetry: result ? result.canRetry !== false : true,
        canEnterManually: result ? result.canEnterManually !== false : true,
      });
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      setUrlExtractError({
        message: 'Something went wrong. Please try again or enter the menu manually.',
        canRetry: true,
        canEnterManually: true,
      });
    } finally {
      setUrlExtracting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      navigate('/admin/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div style={{ padding: 48, textAlign: 'center', color: 'rgba(15, 23, 42, 0.6)' }}>
          <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Loading…
        </div>
      </div>
    );
  }

  const showPreview = extractedMenu !== null;

  return (
    <div className="admin-dashboard">
      <header className="admin-header">
        <div className="admin-header-left">
          <div className="admin-header-logo">
            <i className="fas fa-file-import" aria-hidden="true"></i>
            <span>{PROJECT_NAME}</span>
          </div>
          <div>
            <h1>AI Menu Import</h1>
            <p>Upload a menu image or PDF and review it before creating products</p>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminNavTabs />
          <span className="admin-user">
            <i className="fas fa-user-shield" aria-hidden="true"></i>
            {username}
          </span>
          <button onClick={handleLogout} className="btn-logout">
            <i className="fas fa-sign-out-alt" aria-hidden="true"></i>
            Logout
          </button>
        </div>
      </header>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
        {/* Errors are surfaced in an assertive live region so assistive tech
            announces upload / extraction failures (Req 15.3). */}
        <div ref={statusRef} role="alert" aria-live="assertive">
          {validationError && (
            <div className="alert alert-error" id="menuImportError">
              <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {validationError}
            </div>
          )}
          {extractError && (
            <div className="alert alert-error">
              <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {extractError.message}
              <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {extractError.canRetry && (
                  <button
                    type="button"
                    onClick={runExtraction}
                    disabled={extracting || !selectedFile}
                    style={btnPrimaryStyle}
                  >
                    <i className="fas fa-redo" aria-hidden="true"></i> Try again
                  </button>
                )}
                {extractError.canEnterManually && (
                  <button type="button" onClick={handleEnterManually} style={btnSecondaryStyle}>
                    <i className="fas fa-keyboard" aria-hidden="true"></i> Enter menu manually
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {showPreview ? (
          <div>
            <div style={{ marginBottom: 16 }}>
              <button type="button" onClick={handleStartOver} style={btnSecondaryStyle}>
                <i className="fas fa-arrow-left" aria-hidden="true"></i> Start over
              </button>
            </div>
            <PreviewEditScreen
              menu={extractedMenu}
              isManualEntry={manualEntry}
              onExit={handleStartOver}
              embeddedImages={extractedImages}
              flatScan={extractedFlatScan}
            />
          </div>
        ) : (
          <section
            aria-label="Import menu"
            style={{
              background: '#fff',
              borderRadius: 12,
              border: '1px solid rgba(148, 163, 184, 0.25)',
              padding: 24,
            }}
          >
            {/* Tab switcher — additive UI, file tab is default and unchanged */}
            <div
              role="tablist"
              aria-label="Import method"
              style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid rgba(148,163,184,0.2)' }}
            >
              <button
                role="tab"
                aria-selected={importTab === 'file'}
                type="button"
                onClick={() => { setImportTab('file'); setUrlError(''); setUrlExtractError(null); }}
                style={{
                  padding: '10px 20px',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  background: 'none',
                  border: 'none',
                  borderBottom: importTab === 'file' ? '2px solid #4c1d95' : '2px solid transparent',
                  color: importTab === 'file' ? '#4c1d95' : 'rgba(15,23,42,0.6)',
                  cursor: 'pointer',
                  marginBottom: -2,
                }}
              >
                <i className="fas fa-upload" aria-hidden="true"></i> Upload File
              </button>
              <button
                role="tab"
                aria-selected={importTab === 'url'}
                type="button"
                onClick={() => { setImportTab('url'); setValidationError(''); setExtractError(null); }}
                style={{
                  padding: '10px 20px',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  background: 'none',
                  border: 'none',
                  borderBottom: importTab === 'url' ? '2px solid #4c1d95' : '2px solid transparent',
                  color: importTab === 'url' ? '#4c1d95' : 'rgba(15,23,42,0.6)',
                  cursor: 'pointer',
                  marginBottom: -2,
                }}
              >
                <i className="fas fa-globe" aria-hidden="true"></i> Import from URL
              </button>
            </div>

            {/* ── FILE UPLOAD TAB ─────────────────────────────────────────── */}
            {importTab === 'file' && (
              <>
                <h2 style={{ marginTop: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>
                  Upload a menu document
                </h2>
                <p style={{ color: 'rgba(15, 23, 42, 0.7)', marginTop: 4 }}>
                  Choose a single {SUPPORTED_TYPES_LABEL} file up to {MAX_UPLOAD_MB} MB. Nothing is saved
                  until you review and confirm.
                </p>

            <div className="form-group" style={{ marginTop: 16 }}>
              <label htmlFor="menuImportFile" style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>
                Menu file
              </label>
              <input
                id="menuImportFile"
                ref={fileInputRef}
                type="file"
                accept={FILE_ACCEPT_ATTR}
                onChange={handleFileChange}
                disabled={extracting}
                aria-invalid={validationError ? true : undefined}
                aria-describedby={validationError ? 'menuImportError menuImportHelp' : 'menuImportHelp'}
              />
              <small id="menuImportHelp" className="form-help" style={{ display: 'block', marginTop: 6, color: 'rgba(15, 23, 42, 0.6)' }}>
                Supported types: {SUPPORTED_TYPES_LABEL}. Maximum size: {MAX_UPLOAD_MB} MB.
              </small>
            </div>

            {selectedFile && !validationError && (
              <div style={{ marginTop: 8, fontSize: '0.9rem', color: 'rgba(15, 23, 42, 0.75)' }}>
                <i className="fas fa-file" aria-hidden="true"></i>{' '}
                {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(2)} MB)
              </div>
            )}

            <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={runExtraction}
                disabled={!selectedFile || extracting}
                style={{ ...btnPrimaryStyle, opacity: !selectedFile || extracting ? 0.5 : 1 }}
              >
                {extracting ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Extracting…
                  </>
                ) : (
                  <>
                    <i className="fas fa-magic" aria-hidden="true"></i> Extract menu
                  </>
                )}
              </button>
              {selectedFile && !extracting && (
                <button type="button" onClick={resetSelection} style={btnSecondaryStyle}>
                  Clear
                </button>
              )}
            </div>

            {/* Progress indicator shown while extraction runs until it
                succeeds or fails (Req 1.6). */}
            {extracting && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  marginTop: 20,
                  padding: 16,
                  background: 'rgba(76, 29, 149, 0.06)',
                  borderRadius: 8,
                  border: '1px solid rgba(76, 29, 149, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  color: '#4c1d95',
                  fontWeight: 600,
                }}
              >
                <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                <span>
                  {extractProgress && extractProgress.total > 1 ? (
                    extractProgress.waiting ? (
                      <>
                        Reached the free-tier rate limit — pausing briefly, then
                        continuing with page {extractProgress.current} of {extractProgress.total}.
                        This is normal for large menus; please keep this tab open.
                      </>
                    ) : (
                      <>
                        Extracting page {extractProgress.current} of {extractProgress.total}…
                        multi-page menus are processed one page at a time, so this can
                        take a few minutes. Please keep this tab open.
                      </>
                    )
                  ) : (
                    'Reading your menu and extracting items… this can take a moment.'
                  )}
                </span>
              </div>
            )}
            </>
            )}

            {/* ── URL IMPORT TAB ──────────────────────────────────────────── */}
            {importTab === 'url' && (
              <>
                <h2 style={{ marginTop: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>
                  Import from a restaurant website
                </h2>
                <p style={{ color: 'rgba(15, 23, 42, 0.7)', marginTop: 4 }}>
                  Paste the URL of a restaurant menu page. The page is rendered in a
                  headless browser, menu items and images are extracted by AI, and
                  then shown for review before anything is saved.
                </p>

                <div role="alert" aria-live="assertive">
                  {urlError && (
                    <div className="alert alert-error" style={{ marginTop: 12 }}>
                      <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {urlError}
                    </div>
                  )}
                  {urlExtractError && (
                    <div className="alert alert-error" style={{ marginTop: 12 }}>
                      <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {urlExtractError.message}
                      <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        {urlExtractError.canRetry && (
                          <button
                            type="button"
                            onClick={runUrlExtraction}
                            disabled={urlExtracting}
                            style={btnPrimaryStyle}
                          >
                            <i className="fas fa-redo" aria-hidden="true"></i> Try again
                          </button>
                        )}
                        {urlExtractError.canEnterManually && (
                          <button type="button" onClick={handleEnterManually} style={btnSecondaryStyle}>
                            <i className="fas fa-keyboard" aria-hidden="true"></i> Enter menu manually
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group" style={{ marginTop: 16 }}>
                  <label htmlFor="menuImportUrl" style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>
                    Menu page URL
                  </label>
                  <input
                    id="menuImportUrl"
                    type="url"
                    value={urlInput}
                    placeholder="https://www.example-restaurant.com/menu"
                    onChange={(e) => { setUrlInput(e.target.value); setUrlError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !urlExtracting) runUrlExtraction(); }}
                    disabled={urlExtracting}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: urlError ? '1px solid #ef4444' : '1px solid rgba(148,163,184,0.5)',
                      borderRadius: 8,
                      fontSize: '0.95rem',
                      boxSizing: 'border-box',
                    }}
                    aria-invalid={urlError ? true : undefined}
                  />
                  <small style={{ display: 'block', marginTop: 6, color: 'rgba(15,23,42,0.6)' }}>
                    Works best with static menu pages. JavaScript-heavy sites (Wix, Squarespace) are
                    rendered via headless Chrome — may take up to 25 seconds.
                  </small>
                </div>

                <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={runUrlExtraction}
                    disabled={!urlInput.trim() || urlExtracting}
                    style={{ ...btnPrimaryStyle, opacity: !urlInput.trim() || urlExtracting ? 0.5 : 1 }}
                  >
                    {urlExtracting ? (
                      <>
                        <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Extracting…
                      </>
                    ) : (
                      <>
                        <i className="fas fa-globe" aria-hidden="true"></i> Extract from URL
                      </>
                    )}
                  </button>
                  {urlInput && !urlExtracting && (
                    <button type="button" onClick={() => { setUrlInput(''); setUrlError(''); setUrlExtractError(null); }} style={btnSecondaryStyle}>
                      Clear
                    </button>
                  )}
                </div>

                {urlExtracting && (
                  <div
                    role="status"
                    aria-live="polite"
                    style={{
                      marginTop: 20,
                      padding: 16,
                      background: 'rgba(76, 29, 149, 0.06)',
                      borderRadius: 8,
                      border: '1px solid rgba(76, 29, 149, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      color: '#4c1d95',
                      fontWeight: 600,
                    }}
                  >
                    <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                    <span>Rendering the page and extracting items… this can take up to 25 seconds.</span>
                  </div>
                )}
              </>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

const btnPrimaryStyle = {
  padding: '10px 18px',
  background: '#4c1d95',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: '0.95rem',
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
};

const btnSecondaryStyle = {
  padding: '10px 18px',
  background: '#fff',
  color: '#4c1d95',
  border: '1px solid rgba(76, 29, 149, 0.35)',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: '0.95rem',
  fontWeight: 600,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
};
